const getAnalyticsConfig = require('../../../common/getAnalyticsConfig');
const getAnalyticsEventCenter = require('../../../common/getAnalyticsEventCenter');
const getBaseConfig = require('../../../common/getBaseConfig');
const { isVisitorFromEmail, isOnsiteInfluencedCustomer, markVisitorFromEmail } = require('./session');

const analyticsEventCenter = getAnalyticsEventCenter();
const analyticsConfig = getAnalyticsConfig();
const baseConfig = getBaseConfig();

/**
 * Sends an analytics event to the specified endpoint. Tries
 * `navigator.sendBeacon` first for background delivery. Falls back
 * to `fetch` if Beacon is unavailable or fails. The `params`
 * argument is optional; if omitted, an empty object is sent.
 *
 * @param {string} endpointUrl - Target URL for the analytics event.
 * @param {Object} [params={}] - Optional event data to send.
 * @returns {void}
 */
function sendTrackingEvent(endpointUrl, params = {}) {
    const data = JSON.stringify(params);

    // Try Beacon first
    if (navigator.sendBeacon) {
        const blob = new Blob([data], {
            type: 'application/json'
        });
        const ok = navigator.sendBeacon(endpointUrl, blob);
        if (ok) return;
    }

    // Fallback to fetch
    fetch(endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: data
    }).catch(() => {});
}

/**
 * Track the referral info
 * @param {Object} payload The pageviewLoaded event payload
 * @param {string} [payload.categoryName] The name of the category of PLP
 * @param {string} [payload.pid] The ID of the main product of PDP
 * @param {string} [payload.searchKeywords] The search term used in
 * Search-Show
 * @param {Object} payload.referral Referral info added by middleware function
 * @param {string} payload.referral.referrerUrl Referrer url
 * @param {string} payload.referral.source Source
 */
function trackAbandonedCartEmailVisitor({ referral } = {}) {
    const isAbandonedCartEmailVisitor = referral.source === analyticsConfig.referral.sources.abandonedCartEmail;
    if (isAbandonedCartEmailVisitor && !isVisitorFromEmail()) {
        markVisitorFromEmail();
        sendTrackingEvent(
            baseConfig.abandonedCartConfig.analytics.urls.trackEmailVisitor
        );
    }
}

/**
 * Track the impression of any abandoned cart modal.
 *
 * @param {Object} payload The payload of the event
 * @param {string} payload.modalType The type of the modal (consent or save
 * my basket for guest users)
 */
function trackModalImpression({ modalType } = {}) {
    sendTrackingEvent(
        baseConfig.abandonedCartConfig.analytics.urls.trackModalImpression,
        {
            modalType: modalType
        }
    );
}

/**
 * Track the interaction with any abandoned cart modal.
 *
 * @param {Object} payload The payload of the event
 * @param {string} payload.interaction The type of interaction
 * @param {string} payload.modalType The type of the modal (consent or save
 * my basket for guest users)
 */
function trackModalInteraction({ modalType, interaction } = {}) {
    const payloads = analyticsEventCenter.PAYLOADS.abandonedCartModalInteracted;
    const isPositiveInteraction = (
        interaction === payloads.interaction.continueShopping
        || interaction === payloads.interaction.submitEmailSuccess
    );
    sendTrackingEvent(
        baseConfig.abandonedCartConfig.analytics.urls.trackModalInteraction,
        {
            modalType: modalType,
            isPositiveInteraction
        }
    );
}

/**
 * Initialize the tracking related to abandonedCart
 */
function init() {
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.pageviewLoaded,
        trackAbandonedCartEmailVisitor
    );
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.abandonedCartModalImpression,
        trackModalImpression
    );
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.abandonedCartModalInteracted,
        trackModalInteraction
    );
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.purchased,
        /**
         * Track the order that has been finished after recovering a cart or
         * being influenced
         *
         * @param {Object} payload Purchased event info
         * @param {string} payload.allCouponsApplied The coupons applied to each
         * item of the order
         * @param {string} payload.countryCode The country code of the purchase
         * @param {string} payload.currencyCode The current currency code
         * @param {ProductFieldObject.ProductFieldObject[]} payload.items Product
         * field objects of all the items in cart
         * @param {string} payload.grandTotal The grand total price gross of tax
         * for LineItemCtnr, in purchase currency. Total prices represent the sum of
         * product prices, services prices and adjustments. (Decimal price)
         * @param {string} payload.orderNumber The number of the new order
         * @param {string} payload.totalShippingCost The shipping total price. If
         * the line item container is based on net pricing the shipping total net
         * price is returned. If the line item container is based on gross pricing
         * the shipping total gross price is returned. (Decimal price)
         * @param {string} payload.totalTax The grand total tax for LineItemCtnr,
         * in purchase currency. Total prices represent the sum of product prices,
         * services prices and adjustments. (Decimal price)
         */
        function trackOrder(payload) {
            if (isVisitorFromEmail()) {
                // Recovered cart from Email
                sendTrackingEvent(
                    baseConfig.abandonedCartConfig.analytics.urls.trackRecoveredCartOrder,
                    {
                        grandTotal: payload.grandTotal
                    }
                );
            } else if (isOnsiteInfluencedCustomer()) {
                sendTrackingEvent(
                    baseConfig.abandonedCartConfig.analytics.urls.trackOnsiteInfluencedOrder,
                    {
                        grandTotal: payload.grandTotal
                    }
                );
            }
        }
    );
}

module.exports = {
    init
};
